import React, { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";
import { removeBidderFromProcurement, updateProcurement } from "../../services/api/procurementService";
import SpinnerIcon from "../../assets/icons/spinner";

const CONTACT_INIT = {
  civility: "",
  fullname: "",
  job: "",
  mobile: "",
  emails: [""],
};

function isEmailValid(email = "") {
  const e = String(email);
  return e.length === 0 ? false : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function emailLabel(count) {
  return count > 1 ? "Emails" : "Email";
}

function isContactValid(c) {
  const hasBasics
    = (c?.civility || "").trim()
    && (c?.fullname || "").trim()
    && (c?.job || "").trim();

  const validEmails = (c?.emails || [])
    .map(e => String(e || "").trim())
    .filter(e => isEmailValid(e));

  return hasBasics && validEmails.length >= 1;
}

function ContactFormGroup({ draft, onChange, onRemove }) {
  function updateField(field, newVal) {
    onChange({ ...draft, [field]: newVal });
  }

  function updateEmailAt(index, newVal) {
    const next = [...(draft.emails || [])];
    next[index] = newVal;
    updateField("emails", next);
  }

  function addEmail() {
    updateField("emails", [...(draft.emails || []), ""]);
  }

  function removeEmail(index) {
    const next = (draft.emails || []).filter((_, i) => i !== index);
    updateField("emails", next.length ? next : [""]);
  }

  function canAddEmail() {
    const arr = draft.emails && draft.emails.length ? draft.emails : [];
    const last = arr[arr.length - 1];
    return isEmailValid(last);
  }

  return (
    <div className="border rounded-xl p-4 bg-white shadow-sm">
      <div className="grid grid-cols-12 gap-4 items-end">
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Civilité *</label>
          <select
            required
            className="w-full border rounded-lg px-3 py-2"
            value={draft.civility}
            onChange={e => updateField("civility", e.target.value)}
          >
            <option value="">Sélectionnez</option>
            <option value="Monsieur">Monsieur</option>
            <option value="Madame">Madame</option>
          </select>
        </div>

        <div className="col-span-3">
          <label className="block text-sm font-medium mb-1">Nom complet *</label>
          <input
            required
            type="text"
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Ex. Jules Simon"
            value={draft.fullname}
            onChange={e => updateField("fullname", e.target.value)}
          />
        </div>

        <div className="col-span-4">
          <label className="block text-sm font-medium mb-1">Fonction *</label>
          <input
            required
            type="text"
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Ex. Responsable achats"
            value={draft.job}
            onChange={e => updateField("job", e.target.value)}
          />
        </div>

        <div className="col-span-3">
          <label className="block text-sm font-medium mb-1">GSM</label>
          <PhoneInput
            international
            defaultCountry="BE"
            value={draft.mobile || ""}
            onChange={val => updateField("mobile", val)}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium mb-2">
          {emailLabel(draft.emails)} *
        </label>
        <div className="space-y-2">
          {(draft.emails || [""]).map((mail, i) => (
            <div key={`email-${i}`} className="flex items-center gap-2">
              <input
                type="email"
                className="flex-1 border rounded-lg px-3 py-2"
                placeholder="email@exemple.com"
                value={mail}
                onChange={e => updateEmailAt(i, e.target.value)}
                required={i === 0}
              />
              <button
                type="button"
                className="text-sm px-3 py-2 border rounded-lg hover:bg-gray-50"
                onClick={() => removeEmail(i)}
              >
                Supprimer
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            marginTop: 16,
            padding: "6px 12px",
            cursor: canAddEmail() ? "pointer" : "not-allowed",
            borderRadius: 16,
            backgroundColor: canAddEmail() ? "#3b82f6" : "#ccc",
            color: "white",
            border: "none",
            fontWeight: "bold",
            fontSize: 14,
            width: "auto",
          }}
          onClick={addEmail}
          disabled={!canAddEmail()}
          title={canAddEmail() ? "Ajouter un email" : "Renseigne d'abord un email valide"}
        >
          + Ajouter un email
        </button>
      </div>

      <div className="mt-4">
        <div className="flex justify-end mt-4">
          <button
            onClick={onRemove}
            className="bg-red-100 text-red-800"
            style={{
              border: "none",
              padding: "4px 18px",
              cursor: "pointer",
              borderRadius: 15,
              fontSize: 13,
              fontWeight: "bold",
            }}
            type="button"
          >
            Supprimer ce contact
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactRecapCard({ contact, contactsTotal, onRemove }) {
  const emails = Array.isArray(contact.emails) ? contact.emails.filter(Boolean) : [];
  const emailCount = emails.length;

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
      <div className="bg-blue-50 px-4 py-3 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            {contact.fullname || "Sans nom"}
          </h3>
          <p className="text-sm text-gray-600">{contact.job || "—"}</p>
        </div>
        {contact.civility && (
          <span className="px-2 py-1 text-xs rounded-full bg-white border text-gray-700">
            {contact.civility}
          </span>
        )}
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="block text-xs text-gray-500">Téléphone</span>
            {contact.mobile
              ? (
                <a
                  href={`tel:${contact.mobile}`}
                  className="text-sm text-gray-800 hover:underline"
                >
                  {contact.mobile}
                </a>
              )
              : (
                <span className="text-sm text-gray-400">—</span>
              )}
          </div>

          <div>
            <span className="block text-xs text-gray-500">
              {emailLabel(emailCount)}
            </span>
            {emailCount > 0
              ? (
                <div className="flex flex-wrap gap-1 mt-1">
                  {emails.map((e, idx) => (
                    <span
                      key={`email-${idx}`}
                      className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 border"
                      title={e}
                    >
                      {e}
                    </span>
                  ))}
                </div>
              )
              : (
                <span className="text-sm text-gray-400">—</span>
              )}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t flex justify-end">
        <Button
          size="small"
          color="red"
          onClick={() => onRemove(contact.emails)}
          disabled={contactsTotal <= 1}
        >
          Déselectionner ce contact
        </Button>
      </div>
    </div>
  );
}

export default function Contacts() {
  const { uuidProcurement } = useParams();
  const { bidder, handleUpdateProcurement } = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  const contacts = useMemo(
    () => Object.values(
      bidder?.contacts.reduce((acc, contact) => {
        const key = contact.fullname;
        if (!acc[key]) {
          acc[key] = {
            fullname: contact.fullname,
            emails: [],
            mobile: contact.mobile,
            civility: contact.civility,
            job: contact.job,
          };
        }
        acc[key].emails.push(contact.email);
        return acc;
      }, {}),
    ),
    [bidder],
  );

  function addNewContact() {
    if (draft) return;
    setDraft(CONTACT_INIT);
  }

  function updateDraft(next) {
    setDraft(next);
  }

  function abandonDraft() {
    setDraft(null);
  }

  async function handleSave() {
    try {
      if (!draft || !isContactValid(draft)) return;

      setIsLoading(true);

      const next = [...contacts, draft];
      const payload = {
        bidders: [
          {
            enterpriseNumber: bidder.enterpriseNumber,
            contacts: next,
          },
        ],
      };

      await updateProcurement(uuidProcurement, payload);
      await handleUpdateProcurement();

      setDraft(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function removeExisting(emails) {
    try {
      setIsLoading(true);
      await removeBidderFromProcurement(uuidProcurement, emails);
      await handleUpdateProcurement();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  const canValidateDraft = draft ? isContactValid(draft) : false;

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-bold text-gray-800">
        {bidder?.name || "Nom du soumissionnaire inconnu"}
      </h2>

      <section>
        <Card title={`Contact${contacts.length > 1 ? "s" : ""} du soumissionnaire`}>
          {
            isLoading
              ? (
                <div className="flex w-full justify-center">
                  <div className="w-20">
                    <SpinnerIcon />
                  </div>
                </div>
              )
              : contacts.length === 0
                ? (
                  <p className="text-sm text-gray-600">
                    Aucun contact n'est encore enregistré pour ce soumissionnaire.
                  </p>
                )
                : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {contacts.map((contact, i) => (
                      <ContactRecapCard
                        key={`contact-${i}`}
                        contact={contact}
                        contactsTotal={contacts.length}
                        onRemove={removeExisting}
                      />
                    ))}
                  </div>
                )
          }
        </Card>
      </section>

      <section>
        <Card title="Ajouter un contact au soumissionnaire">
          {draft
            ? (
              <div className="space-y-4">
                <ContactFormGroup draft={draft} onChange={updateDraft} onRemove={abandonDraft} />

                <div className="flex items-center gap-3">
                  <Button onClick={handleSave} color="blue" disabled={!canValidateDraft || isLoading}>
                    Valider ce contact
                  </Button>
                  <Button onClick={abandonDraft} disabled={isLoading} color="lightGray">
                    Annuler
                  </Button>
                  {isLoading && (
                    <div className="w-10">
                      <SpinnerIcon />
                    </div>
                  )}
                </div>
              </div>
            )
            : (
              <div className="flex items-center justify-between mt-3">
                <button
                  type="button"
                  className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                  style={{
                    padding: "6px 12px",
                    borderRadius: 16,
                    backgroundColor: "#3b82f6",
                    color: "white",
                    border: "none",
                    fontWeight: "bold",
                    fontSize: 14,
                    width: "auto",
                  }}
                  onClick={addNewContact}
                  title="Ajouter un contact"
                >
                  + Créer
                </button>
              </div>
            )}
        </Card>
      </section>
    </div>
  );
}
